/**
 * FaqDrawer.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경/목적]
 * FAQ 목록을 보여주는 드로어/바텀시트 컴포넌트
 * 모바일에서는 바텀시트, 데스크탑에서는 우측 슬라이드 드로어로 동작
 * 
 * [주요 기능]
 * - Firestore에서 FAQ 목록 실시간 로드
 * - 아코디언 UI로 질문/답변 표시
 * - 스무스 애니메이션 적용
 * - 모바일 최적화 (한 손 UX)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Divider,
  Fade,
  InputBase,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { getAllFAQs } from '../../../services/faqService';
import type { FAQ } from '../../../types/faq.types';

// 페이드인 애니메이션
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface FaqDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function FaqDrawer({ open, onClose }: FaqDrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // FAQ 목록 상태
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  
  // 검색 상태
  const [searchQuery, setSearchQuery] = useState('');

  // FAQ 목록 로드 & 검색어 초기화
  useEffect(() => {
    if (open) {
      loadFAQs();
    } else {
      // 드로어 닫힐 때 검색어 초기화
      setSearchQuery('');
      setExpanded(false);
    }
  }, [open]);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllFAQs({ activeOnly: true, sortOrder: 'asc' });
      setFaqs(data);
    } catch (err) {
      console.error('FAQ 로드 실패:', err);
      setError('FAQ를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 아코디언 확장 핸들러
  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // 검색어 기반 FAQ 필터링
  const filteredFaqs = faqs.filter((faq) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query)
    );
  });

  // 검색어 하이라이팅 함수
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <Box
          component="span"
          key={index}
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.25)',
            borderRadius: 0.5,
            px: 0.3,
          }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  // 검색어 초기화
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // 드로어 콘텐츠
  const drawerContent = (
    <Box
      sx={{
        width: isMobile ? '100%' : 420,
        height: isMobile ? 'auto' : '100%',
        maxHeight: isMobile ? '85vh' : '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: isMobile ? '24px 24px 0 0' : 0,
        overflow: 'hidden',
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
        }}
      >
        {/* 모바일 드래그 핸들 */}
        {isMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.3)',
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HelpIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.5px' }}>
              자주 묻는 질문
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              궁금한 점을 확인해보세요
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* 검색 영역 */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: '#f8f9fc',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#fff',
            borderRadius: 2,
            border: '1px solid',
            borderColor: searchQuery ? '#667eea' : '#e0e0e0',
            px: 1.5,
            py: 0.5,
            transition: 'all 0.2s ease',
            '&:focus-within': {
              borderColor: '#667eea',
              boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
            },
          }}
        >
          <SearchIcon sx={{ color: searchQuery ? '#667eea' : '#9e9e9e', fontSize: 20 }} />
          <InputBase
            placeholder="질문 또는 답변 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              fontSize: 14,
              '& input': {
                p: 0.5,
              },
            }}
          />
          {searchQuery && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{
                p: 0.5,
                color: '#9e9e9e',
                '&:hover': { color: '#667eea' },
              }}
            >
              <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
        
        {/* 검색 결과 카운트 */}
        {searchQuery && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${filteredFaqs.length}개 결과`}
              size="small"
              sx={{
                height: 22,
                fontSize: 12,
                bgcolor: filteredFaqs.length > 0 ? 'rgba(102, 126, 234, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                color: filteredFaqs.length > 0 ? '#667eea' : '#f44336',
                fontWeight: 600,
              }}
            />
            {filteredFaqs.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                다른 검색어로 시도해보세요
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* 콘텐츠 영역 */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          // 커스텀 스크롤바
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: '#f5f5f5',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#ccc',
            borderRadius: 3,
            '&:hover': {
              bgcolor: '#aaa',
            },
          },
        }}
      >
        {/* 로딩 상태 */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: '#667eea' }} />
            <Typography color="text.secondary">FAQ를 불러오는 중...</Typography>
          </Box>
        )}

        {/* 에러 상태 */}
        {error && !loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 6,
              gap: 2,
            }}
          >
            <HelpIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.5 }} />
            <Typography color="error.main" textAlign="center">
              {error}
            </Typography>
            <Box
              component="button"
              onClick={loadFAQs}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                border: '1px solid #667eea',
                borderRadius: 2,
                bgcolor: 'transparent',
                color: '#667eea',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
              다시 시도
            </Box>
          </Box>
        )}

        {/* FAQ 없음 (전체 목록이 비어있는 경우) */}
        {!loading && !error && faqs.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <HelpIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography>등록된 FAQ가 없습니다.</Typography>
          </Box>
        )}

        {/* 검색 결과 없음 */}
        {!loading && !error && faqs.length > 0 && filteredFaqs.length === 0 && searchQuery && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
            <Typography fontWeight={500}>"{searchQuery}"에 대한 결과가 없습니다</Typography>
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              다른 키워드로 검색해보세요
            </Typography>
          </Box>
        )}

        {/* FAQ 목록 - 아코디언 */}
        {!loading && !error && filteredFaqs.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredFaqs.map((faq, index) => (
              <Fade in key={faq.id} timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                <Accordion
                  expanded={expanded === faq.id}
                  onChange={handleAccordionChange(faq.id)}
                  elevation={0}
                  disableGutters
                  sx={{
                    border: '1px solid',
                    borderColor: expanded === faq.id ? '#667eea' : '#e0e0e0',
                    borderRadius: '12px !important',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    animation: `${fadeInUp} 0.4s ease-out`,
                    animationDelay: `${index * 50}ms`,
                    animationFillMode: 'backwards',
                    '&:before': { display: 'none' },
                    '&:hover': {
                      borderColor: '#667eea',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          color: expanded === faq.id ? '#667eea' : '#9e9e9e',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                    }
                    sx={{
                      px: 2.5,
                      py: 1,
                      minHeight: 56,
                      bgcolor: expanded === faq.id ? 'rgba(102, 126, 234, 0.04)' : 'transparent',
                      '& .MuiAccordionSummary-content': {
                        my: 1.5,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Typography
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: expanded === faq.id ? '#667eea' : '#e3e8f0',
                          color: expanded === faq.id ? '#fff' : '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          flexShrink: 0,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Q
                      </Typography>
                      <Typography
                        component="div"
                        fontWeight={600}
                        sx={{
                          color: expanded === faq.id ? '#667eea' : '#333',
                          fontSize: 15,
                          lineHeight: 1.5,
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {highlightText(faq.question, searchQuery)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      px: 2.5,
                      py: 2,
                      bgcolor: '#fafbfc',
                      borderTop: '1px solid #f0f0f0',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Typography
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: '#764ba2',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        A
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          color: '#555',
                          fontSize: 14,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {highlightText(faq.answer, searchQuery)}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Fade>
            ))}
          </Box>
        )}
      </Box>

      {/* 푸터 */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          bgcolor: '#fafbfc',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          찾는 답변이 없으신가요?{' '}
          <Box
            component="a"
            href="https://pf.kakao.com/_QXtCxj/chat"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#667eea',
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            카카오톡으로 문의하기
          </Box>
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '24px 24px 0 0' : 0,
          maxHeight: isMobile ? '85vh' : '100%',
          boxShadow: isMobile
            ? '0 -8px 32px rgba(0,0,0,0.15)'
            : '-8px 0 32px rgba(0,0,0,0.1)',
        },
      }}
      SlideProps={{
        timeout: 350,
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
